/**
 * PhotoCaptionInput — inline caption editor for maintenance photos.
 *
 * FT-343 (2026-07-08). See DESIGN-BRIEF.md §1.2.
 *
 * Behaviour:
 *  - Max 100 chars (hard-cap; extra keystrokes discarded).
 *  - Save on blur OR when the user presses Enter.
 *  - `Escape` reverts to the initial value and blurs.
 *  - Only mounted when the parent has `caption={true}`.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';

const MAX_LEN = 100;
const PLACEHOLDER = 'Add caption (before / after / etc)';

/**
 * @param {Object} props
 * @param {string} [props.value]
 * @param {(next: string) => void} props.onSave  Called with the trimmed caption.
 * @param {boolean} [props.disabled]
 * @param {string} [props.className]
 * @param {string} [props.testId]
 */
export function PhotoCaptionInput({ value = '', onSave, disabled, className = '', testId = 'photo-caption-input' }) {
  const [draft, setDraft] = useState(value);
  const initialRef = useRef(value);
  const inputRef = useRef(null);

  // Prop-value change (e.g. parent re-hydrated the record) — sync into local draft.
  useEffect(() => {
    initialRef.current = value;
    setDraft(value);
  }, [value]);

  const commit = useCallback(() => {
    const trimmed = (draft || '').trim();
    // Only fire onSave if the value actually changed — avoids parent churn.
    if (trimmed !== (initialRef.current || '').trim()) {
      onSave?.(trimmed);
      initialRef.current = trimmed;
    }
  }, [draft, onSave]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        inputRef.current?.blur();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setDraft(initialRef.current || '');
        // Blur without commit — Escape means "revert".
        // Do this in a microtask so React reflects the revert first.
        Promise.resolve().then(() => inputRef.current?.blur());
      }
    },
    [],
  );

  const handleChange = useCallback((e) => {
    const next = e.target.value;
    // Hard-cap enforcement — silently truncate.
    setDraft(next.length > MAX_LEN ? next.slice(0, MAX_LEN) : next);
  }, []);

  return (
    <input
      ref={inputRef}
      type="text"
      value={draft}
      onChange={handleChange}
      onBlur={commit}
      onKeyDown={handleKeyDown}
      maxLength={MAX_LEN}
      placeholder={PLACEHOLDER}
      disabled={disabled}
      data-testid={testId}
      className={`w-full text-sm text-gray-700 border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-400 ${className}`}
    />
  );
}

export default PhotoCaptionInput;
